import { useEffect, useRef, useState } from "react";
import Job from "../../models/Job";
import Modal from "../../../../../components/base/Modal";
import { Drawer, DrawerRef } from "../../../../../components/drawers/Drawer";
import { hasAccessToMetro, hasAccessToTechnician, openTechQuickView, useEnsureJobColors, useEnvironment, useHub, usePreferences, useScreen } from "../../../../../core/Global";
import { API_ROUTES } from "../../../../../core/Constants";
import TechAssignMap from "../../../../../components/map/TechAssignMap";
import Technician from "../../../../technicians/models/technician";
import { HereGeocodePosition } from "../../../../../models/MapModels";
import Select from "../../../../../components/form/Select";
import TextArea from "../../../../../components/form/TextArea";
import FlexRow from "../../../../../components/base/FlexRow";
import Icon from "../../../../../components/base/Icon";
import Indicator from "../../../../../components/base/Indicator";
import IconButton from "../../../../../components/base/IconButton";
import JobMetro from "../../../base/models/JobMetro";
import TechPriorityBadges from "../../../../../components/tech/TechPriorityBadges";
import { TechSortBy, useTechRanking } from "../../../../../components/map/useTechRanking";
import { renderMetroOption, renderMetroSelection, useMetroOptions } from "../../../base/components/MetroOption";
import TechColorDot from "../TechColorDot";
import "./JobUpdateTechModal.css";

// Whether the map was open when the drawer was last used, on this browser.
const MAP_OPEN_KEY = "jobUpdateTech.mapOpen";
// The list's order (distance or priority), kept the same way.
const SORT_KEY = "jobUpdateTech.sortBy";


interface IProps {
    job?: Job
    onClose?: () => void
}
export default function JobUpdateTechModal(props: IProps) {

    const screen = useScreen()
    const hub = useHub()
    // For the technicians' job colour dots.
    useEnsureJobColors()
    const env = useEnvironment()
    const pref = usePreferences()

    const drawerRef = useRef<DrawerRef>(null);

    const [working, workingSet] = useState<boolean>(false)
    const [updating, updatingSet] = useState<boolean>(false)
    const [sending, sendingSet] = useState<boolean>(false)
    const [job, jobSet] = useState<Job>();
    useEffect(() => jobSet(props.job), [props.job]);

    // The map panel on the right, shown or hidden with the toolbar button. Closed the first
    // time; after that the drawer opens the way it was last left.
    const [mapOpen, mapOpenSet] = useState<boolean>(() => {
        try { return window.localStorage.getItem(MAP_OPEN_KEY) === "1" } catch { return false }
    });
    const toggleMap = () => {
        mapOpenSet(o => {
            try { window.localStorage.setItem(MAP_OPEN_KEY, o ? "0" : "1") } catch { }
            return !o;
        });
        // After the drawer finishes widening, nudge the Here map to re-measure.
        setTimeout(() => window.dispatchEvent(new Event("resize")), 350);
    };

    const [metros, metrosSet] = useState<JobMetro[]>([]);
    const [selectedMetro, selectedMetroSet] = useState<JobMetro>();
    const metroOptions = useMetroOptions(metros, props.job?.address?.position);
    const fetchMetros = (stateId?: string) => {
        hub.Get(API_ROUTES.JobMetros + "all", res => {
            if (res.success) metrosSet((res.data ?? []).filter((m: JobMetro) => hasAccessToMetro(m.id)));
        });
    }

    const [techs, techsSet] = useState<Technician[]>();
    const [selectedTech, selectedTechSet] = useState<Technician>();
    // Where each technician is, their drive to the job, whether they are free, and the order --
    // the same as on the add-job form. The list is shown once it can be shown in its final order:
    // rows re-sorting under the pointer a second after they appear is worse than a moment's spinner.
    const ranking = useTechRanking(techs, props.job?.address?.position);
    // Whatever the order, technicians free right now (on the clock) stay above the rest.
    const [sortBy, sortBySet] = useState<TechSortBy>(() => {
        try {
            const saved = window.localStorage.getItem(SORT_KEY) as TechSortBy;
            return saved === "priority" ? "priority" : "distance";
        } catch { return "distance" }
    });
    const changeSort = (s: TechSortBy) => {
        sortBySet(s);
        try { window.localStorage.setItem(SORT_KEY, s) } catch { }
    };
    const fetchTechs = (jobId?: string, metroId?: string) => {
        ranking.beginLoad();

        var q = "?jobId=" + jobId;
        if (metroId) q += "&metroId=" + metroId;
        hub.Get("/accounts/Technicians/byjob/" + q, res => {
            if (res.success) {
                techsSet((res.data ?? []).filter((t: Technician) => hasAccessToTechnician(t.id)));
            }
            else ranking.endLoad();
        })
    }

    useEffect(() => {
        if (job?.metroId && metros) {
            metros.forEach(m => {
                if (m.id == job.metroId) selectedMetroSet(m);
            });
        }
    }, [job, metros])

    useEffect(() => {
        if (job?.techId && techs) {
            techs.forEach(t => {
                if (t.id == job.techId) selectedTechSet(t);
            });
        }
    }, [job, techs])

    const [mapCenter, mapCenterSet] = useState<HereGeocodePosition>();
    const [mapZoom, mapZoomSet] = useState<number>(5);

    useEffect(() => {
        if (job) {
            fetchTechs(job.id);
            fetchMetros(job.stateId);
            mapCenterSet(job.position);
            mapZoomSet(8);
        }
    }, [job])

    const submit = (note?: any, send?: boolean) => {

        hub.Post(API_ROUTES.Jobs + "tech", {
            id: job?.id,
            metroId: selectedMetro?.id,
            techId: selectedTech?.id,
            note: note,
            send: send
        }, res => {
            if (res.success) {
                window.closeModal("modal-update-tech-note");
                drawerRef.current?.close();
            }
            workingSet(false)
            updatingSet(false)
            sendingSet(false)
        });
    }

    const searchInput = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    // The picked technician is brought into the middle of the list -- when the drawer opens on
    // the job's technician, and when one is picked off the map -- so the choice is always seen.
    useEffect(() => {
        const list = listRef.current
        if (!list || !selectedTech?.id) return
        const row = list.querySelector<HTMLElement>(`.update-tech-row[data-tech-id="${selectedTech.id}"]`)
        if (!row) return
        const top = row.offsetTop - (list.clientHeight - row.offsetHeight) / 2
        list.scrollTo({ top: Math.max(0, top), behavior: "smooth" })
    }, [selectedTech?.id, techs, ranking.ready])
    const [keyword, keywordSet] = useState<string>("")
    // The row under the pointer: its technician's pin is lifted on the map.
    const [hoveredTechId, hoveredTechIdSet] = useState<string>()


    // Changing the technician on an already-closed job re-applies the payment
    // rates for the new tech, so we warn once before proceeding.
    const rateWarnedRef = useRef<boolean>(false)
    const pendingSendRef = useRef<boolean | undefined>(undefined)

    const UpdateTech = (send?: boolean) => {

        if (!selectedMetro?.id) {
            window.showFormError("metroId", "Please select a metro!", true, "drawer-update-tech");
            return;
        }

        if (job?.metroId == selectedMetro?.id && job?.techId == selectedTech?.id) {
            drawerRef.current?.close();
            return;
        }

        if (job?.isClosed && selectedTech?.id && selectedTech.id !== job?.techId && !rateWarnedRef.current) {
            pendingSendRef.current = send;
            window.openModal("modal-tech-rate-warning");
            return;
        }

        if (job?.techId && !selectedTech) {
            window.openModal("modal-remove-tech-note");
            setTimeout(() => {
                $("#txtTechRemoveNote").focus();
            }, 500);
            return;
        }

        if (pref?.jobsPreferences.forceNoteOnUpdateTech) {

            if (job?.techId) {
                window.openModal("modal-update-tech-note");
                setTimeout(() => {
                    $("#txtTechUpdateNote").focus();
                }, 500);
                return;
            }

        }

        workingSet(true)
        if (send) sendingSet(true)
        else updatingSet(true)

        submit("", send)
    }

    // Compact per-technician working-hours line: colored dot + today's window.
    const renderAvailability = (a?: Technician["availability"]) => {
        if (!a || (a.openNow == null && !a.alwaysAvailable)) return null;

        const range = a.todayFrom && a.todayTo ? `${a.todayFrom} – ${a.todayTo}` : undefined;
        let colorText = "text-gray-500", dotColor = "bg-gray-400", label = "Off today";

        if (a.alwaysAvailable) {
            colorText = "text-success"; dotColor = "bg-success"; label = "Available anytime";
        }
        else if (a.openNow) {
            colorText = "text-success"; dotColor = "bg-success";
            label = range ? `Working now · ${range}` : "Working now";
        }
        else if (a.hasHoursToday && range) {
            colorText = "text-warning"; dotColor = "bg-warning"; label = `Off now · ${range}`;
        }

        return (
            <FlexRow className={`fs-8 mt-1 ${colorText}`}>
                <span className={`d-inline-block rounded-circle me-2 ${dotColor}`} style={{ width: 7, height: 7 }} />
                <span>{label}</span>
            </FlexRow>
        );
    };

    return (
        <>

            <Drawer ref={drawerRef} id="drawer-update-tech"
                title="Metro & Technician"
                subtitle={"Job ID: #" + job?.jobId + " | " + job?.fullName}
                size="medium"
                toolbarComponent={() => (
                    screen.isMobile ? <></> :
                        <IconButton icon="map-location-dot" size={26} gap={14}
                            type={mapOpen ? "solid" : "light"}
                            title={mapOpen ? "Hide map" : "Show map"}
                            onClick={toggleMap} />
                )}
                sidePanelOpen={mapOpen}
                sidePanelWidth={`${Math.min(Math.round(screen.width * .4), 760)}px`}
                sidePanel={
                    job && mapOpen &&
                    <TechAssignMap height={screen.height}
                        jobPosition={props.job?.address?.position}
                        jobTitle={"#" + job.jobId + (job.fullName ? " · " + job.fullName : "")}
                        techs={techs} selectedTech={selectedTech} hoveredTechId={hoveredTechId}
                        routeLabel={selectedTech ? ranking.infoFor(selectedTech).routeLabel : undefined}
                        metros={metros} selectedMetro={selectedMetro}
                        // A technician on the map opens as on the jobs board, with one thing
                        // more: a button that picks them here. Somebody not on this job's list
                        // (another metro, no matching skill) is added to it, selected. The
                        // drawer closes itself, through its own script, after calling this.
                        onTechClick={tech => openTechQuickView(tech.id, picked => {
                            const own = techs?.find(t => t.id === picked.id) ?? picked;
                            if (!techs?.some(t => t.id === picked.id)) techsSet([...(techs ?? []), picked]);
                            selectedTechSet(own);
                        })}
                        onSelectMetro={mtr => {
                            if (env.isDevelopment) console.log("job-updatetech-map metro changed", mtr);
                            selectedMetroSet(mtr);
                            selectedTechSet(undefined);
                            fetchTechs(job?.id, mtr.id);
                        }} />
                }
                noScroll
                onOpen={() => {
                    setTimeout(() => searchInput.current?.focus(), 200);
                }}
                onClose={props.onClose}>
                {/* A column the full height of the drawer: metro and search on top, the
                    technician list taking whatever is left and scrolling inside itself, and the
                    buttons pinned at the bottom -- however tall the window is. */}
                <div className="update-tech-column">
                    {/* Metro */}
                    <FlexRow>
                        <label className="flex-grow-1 fw-bold fs-7" htmlFor="drp-jobdashboard-metros">
                            Metro
                            <span id="metroId-err" className="small text-danger mx-3 form-error"></span>
                        </label>
                    </FlexRow>

                    <Select id="drp-jobdashboard-metros" name="metroId"
                        labelUp marginBottom={0} source={metroOptions} deselect
                        optionTemplate={renderMetroOption} selectionTemplate={renderMetroSelection}
                        value={selectedMetro?.id ?? job?.metroId}
                        onClear={() => {
                            selectedTechSet(undefined);
                            techsSet(undefined);
                            selectedMetroSet(undefined);
                        }}
                        onSelectItem={item => {
                            if (!item) return;
                            selectedMetroSet(item);
                            selectedTechSet(undefined);
                            fetchTechs(job?.id, item.id);
                            mapZoomSet(7);
                        }} />

                    {/* Technician */}
                    <FlexRow className="mt-5">
                        <label className="fw-bold fs-7 flex-grow-1">
                            Technician
                            <span id="techId-err" className="small text-danger mx-3 form-error"></span>
                        </label>
                        {/* Distance or priority (metro and job type together); free-now technicians stay on top either way. */}
                        <button type="button" className="btn btn-link p-0 fs-8 text-muted text-hover-primary text-nowrap"
                            title={sortBy === "distance" ? "Nearest first. Click to sort by metro + job type priority" : "Metro + job type priority first. Click to sort by distance"}
                            onClick={() => changeSort(sortBy === "distance" ? "priority" : "distance")}>
                            <Icon icon="arrow-down-short-wide" className="me-1" size={11} />
                            {sortBy === "distance" ? "Distance" : "Priority"}
                        </button>
                    </FlexRow>

                    <div className="mt-3 mb-2">
                        <input ref={searchInput} className="form-control form-control-sm" placeholder="Search technician..."
                            value={keyword} onChange={e => keywordSet(e.target.value)} />
                    </div>

                    <div ref={listRef} className="scroll-y update-tech-list">
                        {
                            !ranking.ready &&
                            <div className="text-center py-20">
                                <Indicator />
                                <div className="fs-8 text-muted mt-3">Working out driving times…</div>
                            </div>
                        }
                        {
                            ranking.ready && job?.techId &&
                            <label className="btn btn-outline btn-outline-dashed d-flex flex-stack text-start py-3 px-4 mb-2">
                                <div className="d-flex align-items-center me-2">
                                    <div className="form-check form-check-custom form-check-solid form-check-primary form-check-sm me-3">
                                        <input className="form-check-input form-check-input-sm"
                                            type="radio" checked={!selectedTech?.id}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    selectedTechSet(undefined);
                                                }
                                            }} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <h2 className="d-flex align-items-center fs-7 fw-bolder flex-wrap mb-1 text-danger">
                                            No Technician
                                        </h2>
                                    </div>
                                </div>
                                <div className="ms-5">
                                </div>
                            </label>
                        }
                        {
                            ranking.ready &&
                            // The currently-assigned tech stays pinned on top; the rest free-now
                            // first, then in the order picked above, then by name.
                            ranking.rank(techs?.filter(f => f.fullName.toLowerCase().includes(keyword.toLocaleLowerCase())), props.job?.techId, sortBy)
                                .map(({ item, info }, k) => {
                                    return (
                                    <label key={k} data-tech-id={item.id} className={"btn btn-outline btn-outline-dashed d-flex flex-stack text-start py-3 px-4 mb-2 update-tech-row" + (selectedTech?.id === item.id ? " is-selected" : "")}
                                        onMouseEnter={() => hoveredTechIdSet(item.id)}
                                        onMouseLeave={() => hoveredTechIdSet(h => h === item.id ? undefined : h)}>
                                        <div className="d-flex align-items-center me-2">
                                            <div className="form-check form-check-custom form-check-solid form-check-primary form-check-sm me-3">
                                                <input className="form-check-input form-check-input-sm"
                                                    type="radio" checked={selectedTech?.id == item.id}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            selectedTechSet(item);
                                                        }
                                                    }} />
                                            </div>
                                            <div className="flex-grow-1">
                                                <h2 className="d-flex align-items-center fs-7 fw-bolder flex-wrap mb-1">
                                                    {item.fullName}
                                                    <TechColorDot colorId={item.techJobColorId} />
                                                    {
                                                        /* On the mobile app: the badge, then where they stand on the clock. */
                                                        info.hasApp &&
                                                        <>
                                                            <span className="badge badge-light-primary fs-9 ms-2" title={info.platform ? `Rinvox app on ${info.platform}` : "Rinvox app"}>
                                                                <Icon icon="mobile-screen" type="solid" size={9} className="me-1" />
                                                                App
                                                            </span>
                                                            <span className={"badge fs-9 ms-1 " + (info.onClock ? "badge-success" : "badge-light")}
                                                                title={info.clockAt ? (info.onClock ? "Clocked in " : "Clocked out ") + info.clockAt.toLocaleString() : undefined}>
                                                                <Icon icon="user-clock" type="solid" size={9} className="me-1" />
                                                                {info.onClock ? "On the clock" : "Off the clock"}
                                                            </span>
                                                        </>
                                                    }
                                                </h2>
                                                <div className="fs-8 opacity-50">
                                                    {info.distanceLabel ?? "Unknown Distance"}
                                                    {info.distanceNote && <span className="ms-1">· {info.distanceNote}</span>}
                                                </div>
                                                {renderAvailability(item.availability)}
                                            </div>
                                        </div>
                                        <div className="ms-5 d-flex align-items-center">
                                            <TechPriorityBadges tech={item} className="me-3" />
                                            <span className="fs-8 opacity-50">
                                                {
                                                    item.activeJobs && item.activeJobs > 0 ?
                                                        <span data-kt-element="period">{item.activeJobs} Active Jobs</span>
                                                        : <span data-kt-element="period">
                                                            <Icon icon="circle" type="solid" className="text-success" size={10} />
                                                        </span>
                                                }
                                            </span>
                                        </div>
                                    </label>
                                )
                                })
                        }
                    </div>
                    {/* Actions */}
                    <FlexRow justify="end" className="border-top pt-4 mt-4 update-tech-actions">
                        <button type="button" className="btn btn-secondary btn-sm me-2" disabled={working}
                            onClick={e => {
                                e.preventDefault();
                                drawerRef.current?.close();
                            }}>
                            Discard
                        </button>
                        <button type="button" className="btn btn-sm btn-primary me-2"
                            disabled={working} onClick={e => {
                                e.preventDefault();
                                UpdateTech();
                            }}>
                            {
                                updating ?
                                    <>
                                        <Icon icon="spinner-third" spin />
                                        <span className="ms-2">
                                            Please Wait
                                        </span>
                                    </>
                                    :
                                    <>
                                        Update Metro &amp; Tech
                                    </>
                            }
                        </button>
                        {
                            selectedTech &&
                            <button type="button" className="btn btn-sm btn-success"
                                disabled={working} onClick={e => {
                                    e.preventDefault();
                                    UpdateTech(true);
                                }}>
                                {
                                    sending ?
                                        <>
                                            <Icon icon="spinner-third" spin />
                                            <span className="ms-2">
                                                Please Wait
                                            </span>
                                        </>
                                        :
                                        <>
                                            Update & Send
                                        </>
                                }
                            </button>
                        }
                    </FlexRow>

                </div>
            </Drawer>

            <Modal id="modal-update-tech-note"
                title="Update Technician Note"
                secondModal
                buttonText="Update & Send"
                buttonColor="success"
                onConfirm={callback => {
                    var note = $("#txtTechUpdateNote").val();
                    if (job?.techId && (note?.toString().length ?? 0) < 2) {
                        window.showFormError("techUpdateNote", "Please write a note!", false, "modal-update-tech-note");
                        callback(true);
                        return;
                    }
                    submit(note, true);
                }}
                secondActionText="Update Technician"
                onSecondConfirm={callback => {
                    var note = $("#txtTechUpdateNote").val();
                    if (job?.techId && (note?.toString().length ?? 0) < 2) {
                        window.showFormError("techUpdateNote", "Please write a note!", false, "modal-update-tech-note");
                        callback(true);
                        return;
                    }
                    submit(note);
                }}>
                <div>
                    <p>
                        Please describe why you are changing the thechnician:
                    </p>
                    <TextArea block name="techUpdateNote" id="txtTechUpdateNote" rows={5} />
                </div>
            </Modal>

            <Modal id="modal-remove-tech-note"
                title="Remove Technician"
                secondModal
                buttonText="Confirm & Remove"
                buttonColor="danger"
                onOpen={() => $("#txtTechRemoveNote").focus()}
                onConfirm={callback => {
                    var note = $("#txtTechRemoveNote").val();
                    if ((note?.toString().length ?? 0) < 2) {
                        window.showFormError("techRemoveNote", "Please write a note!", false, "modal-remove-tech-note");
                        callback(true);
                        return;
                    }

                    hub.Post(API_ROUTES.Jobs + "cleartech", {
                        id: props.job?.id,
                        metroId: selectedMetro?.id,
                        note: note
                    }, res => {
                        if (res.success) {
                            if (callback) callback();
                            drawerRef.current?.close();
                        }
                    });

                }}>
                <div>
                    <p>
                        Please describe why you are removing the thechnician:
                    </p>
                    <TextArea block name="techRemoveNote" id="txtTechRemoveNote" rows={5} />
                </div>
            </Modal>

            <Modal id="modal-tech-rate-warning"
                title="Recalculate Job Rates?"
                titleIcon="triangle-exclamation"
                secondModal
                buttonText="Continue"
                buttonColor="warning"
                onConfirm={callback => {
                    rateWarnedRef.current = true;
                    window.closeModal("modal-tech-rate-warning");
                    if (callback) callback();
                    UpdateTech(pendingSendRef.current);
                }}>
                <div className="my-5">
                    This job is already closed. Changing the technician will re-apply
                    the job's payment rates (technician %, tech fee, CC / check / Rinvox
                    rates) based on the new technician — these may differ from the
                    previous one. Do you want to continue?
                </div>
            </Modal>
        </>
    )

}
